import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import type { SupportVideo } from './support.data';
import { useSupportAssetUrl } from './support.urls';

interface VideoPlayerModalProps {
  video: SupportVideo | null;
  isOpen: boolean;
  onClose: () => void;
}

const VideoPlayerModal: React.FC<VideoPlayerModalProps> = ({ video, isOpen, onClose }) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  // Blob-relative videos resolve to a user-SAS-signed playable URL.
  const videoSrc = useSupportAssetUrl(video?.videoUrl);

  useEffect(() => {
    if (!isOpen) return;
    closeButtonRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen || video === null) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="support-modal-backdrop"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className="support-modal"
        role="dialog"
        aria-modal="true"
        aria-label={video.title}
      >
        <button
          ref={closeButtonRef}
          type="button"
          className="support-modal-close"
          onClick={onClose}
          aria-label="Close video player"
        >
          <X size={20} strokeWidth={2.2} />
        </button>
        <div className="support-modal-player">
          {/* Key remounts the player per video; unmount on close stops playback. */}
          <video key={video.id} src={videoSrc || undefined} controls playsInline preload="metadata" />
        </div>
        <h3 className="support-modal-title">{video.title}</h3>
        <p className="support-modal-description">{video.description}</p>
      </div>
    </div>
  );
};

export default VideoPlayerModal;
